import React, { useEffect, useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { actions } from '../../state';
import { config } from '../../api/config';
import {
  generateHeader,
  isEmpty,
  mapServiceTypes,
  isFilterActive,
  buildTableFilterBody,
} from '../../utils';
import {
  SelectFilter,
  WithSpinner,
  Table,
  TableRow,
  TableCell,
} from '../../components';
import { AddressesContext } from './addresses.context copy';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const LocationsTable = ({
  dispatch,
  locations: { locations, header, footer, loading, total },
  timeRangeFilter,
  getFilteredBody,
  clearAllFilters,
  setClearToFalse,
}) => {
  const {
    locationFilter,
    setLocationFilter,
    servicesFilter,
    setServicesFilter,
    sortOrder,
    setSortOrder,
    filterBody,
    setFilterBody,
  } = useContext(AddressesContext);
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const fetchLocations = useCallback(
    data => {
      dispatch(actions.locations.getLocationsAction(data));
    },
    [dispatch]
  );

  const clearLocations = useCallback(() => {
    dispatch(actions.locations.clearLocationsAction());
  }, [dispatch]);

  useEffect(() => () => clearLocations(), [clearLocations]);

  const clearSelectedLocation = useCallback(() => {
    dispatch(actions.locations.setSelectedLocationAction({}));
  }, [dispatch]);

  useEffect(() => {
    let filterToSubmit;

    if (filterBody) {
      if (clearAllFilters) {
        setLocationFilter([]);
        setServicesFilter([]);
        setFilterBody();
        setSortOrder('asc');
        setClearToFalse(false);
        setPage(1);
      } else if (timeRangeFilter) {
        const filterSum = { ...filterBody, ...timeRangeFilter };
        filterToSubmit = filterSum;
      } else {
        filterToSubmit = filterBody;
      }
    } else if (timeRangeFilter) {
      filterToSubmit = timeRangeFilter;
    }
    const offset = (page - 1) * PAGE_SIZE;
    const submitBody = filterToSubmit
      ? { ...filterToSubmit, offset, limit: PAGE_SIZE }
      : { offset, limit: PAGE_SIZE };
    getFilteredBody(submitBody);
    fetchLocations(submitBody);
  }, [
    fetchLocations,
    filterBody,
    timeRangeFilter,
    getFilteredBody,
    clearAllFilters,
    setClearToFalse,
    page,
    setFilterBody,
    setLocationFilter,
    setServicesFilter,
    setSortOrder,
  ]);

  const handleSubmit = submitOptions => {
    const generatedFilter = [];

    if (locationFilter.length) {
      generatedFilter.push({ field: 'full_address', value: locationFilter });
    }
    if (servicesFilter.length) {
      generatedFilter.push({
        field: 'enabled_services',
        value: servicesFilter,
        convert: 'smallint',
        expression: 'overlap',
      });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
        extraBody: timeRangeFilter || {},
      })
    );
    setPage(1);
  };

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const tableHeader = [
    'ID',
    <SelectFilter
      title="Lokacija"
      name="full_address"
      valueType="string"
      array={generateHeader(header.full_address)}
      filterState={locationFilter}
      setFilterState={setLocationFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', locationFilter)}
    />,
    'Miestas',
    <SelectFilter
      title="Įjungtos paslaugos"
      name="enabled_services"
      valueType="number"
      array={generateHeader(header.enabled_services, mapServiceTypes)}
      filterState={servicesFilter}
      setFilterState={setServicesFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', servicesFilter)}
    />,
    'Įrenginių sk.',
    'Sugedę',
  ];

  const tableFooter = [
    '',
    '',
    '',
    '',
    footer?.devices_count ?? '',
    footer?.broken_devices ?? '',
  ];

  const renderTable = () =>
    locations.map(
      ({
        id,
        full_address,
        city,
        enabled_services,
        devices_count,
        broken_devices,
      }) => (
        <TableRow
          onClick={clearSelectedLocation}
          url={config.APP_PATHS.editLocation(id)}
          key={id}
        >
          <TableCell>{id}</TableCell>
          <TableCell>{full_address}</TableCell>
          <TableCell>{city || '-'}</TableCell>
          <TableCell>
            {enabled_services?.map(service => (
              <p key={service}>{mapServiceTypes(service)}</p>
            ))}
          </TableCell>
          <TableCell align="right">{devices_count ?? '-'}</TableCell>
          <TableCell align="right">{broken_devices ?? '-'}</TableCell>
        </TableRow>
      )
    );

  return (
    <WithSpinner loading={loading}>
      {!isEmpty(header) && (
        <Table
          header={tableHeader}
          footer={tableFooter}
          filter={true}
          isEmpty={!locations.length}
          width="xs"
          rightAlignedFooterCells={[4, 5]}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          serverPagination={true}
          onPageChange={setPage}
        >
          {renderTable()}
        </Table>
      )}
    </WithSpinner>
  );
};

const mapStateToProps = state => ({
  locations: state.locations,
});

LocationsTable.propTypes = {
  dispatch: PropTypes.func.isRequired,
  locations: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    locations: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    header: PropTypes.object.isRequired,
    footer: PropTypes.object.isRequired,
  }).isRequired,
  timeRangeFilter: PropTypes.object,
  getFilteredBody: PropTypes.func.isRequired,
  clearAllFilters: PropTypes.bool.isRequired,
  setClearToFalse: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(LocationsTable);
